import React, { useEffect, useRef, useState } from 'react';
import '@progress/kendo-theme-default/dist/all.css';
import '../theme.css';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCellProps, GridPageChangeEvent, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { orderBy } from '@progress/kendo-data-query';
import type { SortDescriptor } from '@progress/kendo-data-query';
import type { FormDefinition } from '../types';

const PAGE_SIZE = 5;

function makeActionsCell(onOpen: (formId: string) => void, onDelete: (definition: FormDefinition) => void) {
  return function ActionsCell(props: GridCellProps) {
    const dataItem = props.dataItem as FormDefinition;
    return (
      <td className={props.className} style={props.style} role="gridcell" aria-colindex={props.ariaColumnIndex}>
        <div className="d-flex justify-content-end" style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onOpen(dataItem.formId)} aria-label="Edit item">
            Edit
          </button>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onDelete(dataItem)} aria-label="Delete item">
            Delete
          </button>
        </div>
      </td>
    );
  };
}

export interface FormLibraryCallbacks {
  /** User clicked Open on a row. */
  onOpenForm: (formId: string) => void;
  /** User clicked "+ New Form". The host creates the FormDefinition + initial draft
   *  FormVersion (e.g. via `newId()`) and is responsible for navigating to it. */
  onCreateForm: () => void;
  /** User confirmed deletion in the dialog. */
  onDeleteForm: (formId: string) => void;
}

export interface FormLibraryProps {
  definitions: FormDefinition[];
  callbacks: FormLibraryCallbacks;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Public component — lists every FormDefinition the host supplies, with Open/Delete
 * actions and a "+ New Form" button. The host owns all persistence; this component only
 * signals user intent via `callbacks`, same contract as FormBuilder/FormRenderer.
 */
export function FormLibrary({ definitions, callbacks, style, className }: FormLibraryProps) {
  const [pendingDelete, setPendingDelete] = useState<FormDefinition | null>(null);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(PAGE_SIZE);
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const ActionsCell = makeActionsCell(callbacks.onOpenForm, setPendingDelete);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const formatPager = () => {
      // "1 - 5 of 10 items" → "Showing: 1-5, of 10"
      const infoEl = container.querySelector<HTMLElement>('.k-pager-info');
      if (infoEl) {
        const t = infoEl.textContent ?? '';
        const m = t.match(/(\d+)\s*[–-]\s*(\d+)\s+of\s+(\d+)/);
        if (m && !t.startsWith('Showing:')) {
          infoEl.textContent = `Showing: ${m[1]}-${m[2]}, of ${m[3]}`;
        }
      }
      // "items per page" (or any text) → "Rows per page:"
      const sizesLabel = container.querySelector<HTMLSpanElement>('.k-pager-sizes > span:not(.k-dropdownlist):not(.k-picker)');
      if (sizesLabel && sizesLabel.textContent !== 'Rows per page:') {
        sizesLabel.textContent = 'Rows per page:';
      }
      // Rename the icon class from chevron to caret-alt so the host app's kendo-custom.css
      // can target it and render the correct Font Awesome glyph (FA hides the SVG via
      // `svg { display: none }` and uses ::before content for the icon shape).
      // The SVG path is intentionally NOT replaced — in dev the Kendo chevron SVG renders
      // as an open chevron (</>), and in the host app the SVG is hidden anyway.
      container.querySelectorAll<HTMLElement>('.k-pager-nav:not(.k-pager-first):not(.k-pager-last)').forEach(btn => {
        const iconSpan = btn.querySelector('.k-svg-icon');
        if (!iconSpan) return;
        if (iconSpan.classList.contains('k-svg-i-chevron-left')) {
          iconSpan.classList.replace('k-svg-i-chevron-left', 'k-svg-i-caret-alt-left');
        } else if (iconSpan.classList.contains('k-svg-i-chevron-right')) {
          iconSpan.classList.replace('k-svg-i-chevron-right', 'k-svg-i-caret-alt-right');
        }
      });
      // First/last page buttons have Kendo's caret-alt-to-left/right (filled triangle + bar).
      // Replace the SVG path with double-chevron paths so they visually match the open-chevron
      // style of the prev/next buttons in dev. The class stays unchanged so production CSS
      // (which hides the SVG and uses Font Awesome \f323/\f324) continues to target them.
      container.querySelectorAll<HTMLElement>('.k-pager-first, .k-pager-last').forEach(btn => {
        const iconSpan = btn.querySelector<HTMLElement>('.k-svg-icon');
        const path = iconSpan?.querySelector('path');
        if (!iconSpan || !path) return;
        if (iconSpan.classList.contains('k-svg-i-caret-alt-to-left') &&
            !iconSpan.dataset.pathReplaced) {
          path.setAttribute('d', 'M289 383 162.9 257 289 130.9 255 97 95 257l160 160zm128 0L290.9 257 417 131l-34-34-160 160 160 160z');
          iconSpan.dataset.pathReplaced = '1';
        } else if (iconSpan.classList.contains('k-svg-i-caret-alt-to-right') &&
            !iconSpan.dataset.pathReplaced) {
          path.setAttribute('d', 'M223 130.9 349.1 257 223 383l34 34 160-160L257 97zm-128 0L221.1 257 95 383l34 34 160-160-160-160z');
          iconSpan.dataset.pathReplaced = '1';
        }
      });

      // Kendo React v15 does not render a refresh button via pageable.refresh — inject it manually.
      const pager = container.querySelector('.k-pager');
      if (pager && !pager.querySelector('.k-pager-refresh')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'k-pager-refresh k-button k-button-md k-button-flat k-button-flat-base k-icon-button';
        btn.setAttribute('title', 'Refresh');
        btn.setAttribute('aria-label', 'Refresh');
        btn.innerHTML =
          '<span class="k-button-icon k-icon k-svg-icon k-svg-i-arrow-rotate-cw" aria-hidden="true">' +
          '<svg viewBox="0 0 512 512" focusable="false" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M480 288c0 123.7-100.3 224-224 224S32 411.7 32 288 132.3 64 256 64V0l160 96-160 96v-64c-88.2 0-160 71.8-160 160s71.8 160 160 160 160-71.8 160-160c0-29.1-7.8-56.4-21.5-80l55.5-32c19.1 32.9 30 71.2 30 112"/>' +
          '</svg></span>';
        pager.appendChild(btn);
      }
    };

    formatPager();
    const obs = new MutationObserver(formatPager);
    obs.observe(container, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, []);

  const sortedData = orderBy(
    definitions.map(d => ({ ...d, statusText: d.isActive ? 'Active' : 'Inactive' })),
    sort
  );
  const pagedData = sortedData.slice(skip, skip + take);

  function handlePageChange(e: GridPageChangeEvent) {
    setSkip(e.page.skip);
    setTake(e.page.take);
  }

  function handleSortChange(e: GridSortChangeEvent) {
    setSort(e.sort);
    setSkip(0);
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 'var(--kendo-spacing-5) var(--kendo-spacing-6)',
        background: 'var(--kendo-color-app-surface)',
        color: 'var(--kendo-color-on-app-surface)',
        ...style,
      }}
      data-fulcrum-form-library
    >
      <div style={{ maxWidth: 1400, width: '100%', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--kendo-spacing-4)' }}>
          <h1 style={{ fontSize: 'var(--kendo-font-size-xl)', margin: 0 }}>Form Library</h1>
          <span style={{ flex: 1 }} />
          <Button themeColor="primary" onClick={callbacks.onCreateForm}>
            + New Form
          </Button>
        </div>
        <Grid
          className="k-grid-scroll-horizontal c-kendo-table-wrapper k-with-pagination"
          data={pagedData}
          total={definitions.length}
          skip={skip}
          take={take}
          pageable={{ buttonCount: 5, pageSizes: [5, 10, 15, 20, 25], info: true }}
          onPageChange={handlePageChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          navigatable={true}
        >
          <GridColumn field="name" title="Name" width={260} />
          {/* No fixed width — Kendo gives unsized columns the remaining space, so this
              column auto-resizes with the available width instead of clipping/wrapping. */}
          <GridColumn field="description" title="Description" />
          <GridColumn field="formType" title="Type" width={120} />
          <GridColumn field="statusText" title="Status" width={110} />
          <GridColumn title=" " width={190} locked cells={{ data: ActionsCell }} />
        </Grid>
      </div>

      {pendingDelete && (
        <Dialog title="Delete form" onClose={() => setPendingDelete(null)}>
          <p>
            Delete <strong>{pendingDelete.name}</strong> and all of its versions? This cannot be undone.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              themeColor="error"
              onClick={() => {
                callbacks.onDeleteForm(pendingDelete.formId);
                setPendingDelete(null);
              }}
            >
              Delete
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
}
